import { useMutation } from 'react-query';

import {
  BackResponse,
  BackResponseFailure,
} from '@savgroup-front-common/types';

import { CommonCarrierService } from '../../../api';
import { useBanners } from '../../NotificationsProvider';
import { prepareAppointmentTimeToCreateHomePickupCommand } from '../adapters/prepareAppointmentTimeToCreateHomePickupCommand';
import { Interval } from '../AppointmentCard.types';
import { haveScheduleProblem } from '../helpers/haveScheduleProblem';
import messages from '../messages';

interface UseCreateHomePickupCommandArgs {
  fileId: string;
  onSuccess?: () => void;
}

const useCreateHomePickupCommand = ({
  fileId,
  onSuccess,
}: UseCreateHomePickupCommandArgs) => {
  const { removeAllNotifications, pushError } = useBanners({
    extendedMessages: messages,
  });

  const { mutateAsync: handleCreateHomePickup, isLoading } = useMutation<
    Promise<BackResponse<any> | BackResponseFailure>,
    unknown,
    { appointmentTime?: Interval }
  >(async ({ appointmentTime }: { appointmentTime?: Interval }) => {
    if (!fileId || !appointmentTime) {
      return undefined;
    }
    removeAllNotifications();

    const response = await CommonCarrierService.createHomePickupCommand({
      fileId,
      appointmentTime: prepareAppointmentTimeToCreateHomePickupCommand({
        appointmentTime,
      }),
    });

    if (response.failure) {
      if (response.errors.some(haveScheduleProblem)) {
        return await handleCreateHomePickup({
          appointmentTime: {
            ...appointmentTime,
            endLocalTime: new Date(
              appointmentTime.endLocalTime.setHours(9, 0, 0, 0),
            ),
            startLocalTime: new Date(
              appointmentTime.startLocalTime.setHours(23, 1, 0, 0),
            ),
          },
        });
      }

      pushError({
        code: 'createHomePickupError',
        message: '',
        subject: '',
        isWarning: false,
      });

      return response;
    }

    if (onSuccess) {
      onSuccess();
    }

    return response;
  });

  return { isLoading, handleCreateHomePickup };
};

export default useCreateHomePickupCommand;
